// ConfirmDialog — destructive confirmation, with a typed-confirm tier.
//
// The old admin used `window.confirm()` for destructive actions. That is
// unstyled, unlabelled, impossible to test through the DOM, and — worst —
// dismissible with a single Enter keypress, so a focused destructive button
// plus a stray Enter deletes a record.
//
// Two tiers, chosen by consequence:
//   'confirm'  — reversible or low-stakes. A styled dialog with a real button.
//   'type'     — irreversible or money-moving. The operator must TYPE the
//                record's name/id before the action button enables.
//
// Built on Radix Dialog so focus trapping, Escape, scroll-lock and
// `aria-modal` are correct without hand-rolling them.

import { Button, cn } from '@fajrarabi/ui';
import * as RadixDialog from '@radix-ui/react-dialog';
import { useEffect, useId, useState, type ReactNode } from 'react';

import { errorMessage } from './ErrorState';
import { Icon } from './Icon';

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  /** Extra content — an amount field, a reason textarea. */
  children?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** `danger` colours the action red; use for anything destructive. */
  tone?: 'danger' | 'primary';
  /** When set, the operator must type this exact string to enable the action. */
  requireTyped?: string;
  /** Label above the type-to-confirm field. */
  requireTypedLabel?: ReactNode;
  onConfirm: () => void | Promise<void>;
  /** Disable the action for caller-side reasons (invalid form). */
  disabled?: boolean;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'danger',
  requireTyped,
  requireTypedLabel,
  onConfirm,
  disabled = false,
}: ConfirmDialogProps) {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const typedId = useId();

  // Reset per opening: a previous attempt's error or half-typed confirmation
  // must never carry into the next one.
  useEffect(() => {
    if (open) {
      setTyped('');
      setError(null);
      setBusy(false);
    }
  }, [open]);

  const typedOk = !requireTyped || typed.trim() === requireTyped.trim();
  const canConfirm = typedOk && !disabled && !busy;

  async function handleConfirm() {
    if (!canConfirm) return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch (err) {
      // Stay OPEN on failure. Closing would hide the reason and leave the
      // operator unsure whether the action took effect.
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <RadixDialog.Root open={open} onOpenChange={busy ? undefined : onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-40 bg-admin-900/40 backdrop-blur-sm" />
        <RadixDialog.Content
          onEscapeKeyDown={(e: KeyboardEvent) => {
            // Escape must not dismiss mid-request: the action may already have
            // reached the server, and closing would hide the outcome.
            if (busy) e.preventDefault();
          }}
          className="fixed left-1/2 top-1/2 z-50 w-[min(30rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-admin-200 bg-white p-6 shadow-xl focus:outline-none"
        >
          <div className="flex items-start gap-3.5">
            <div
              className={cn(
                'grid h-10 w-10 shrink-0 place-items-center rounded-full',
                tone === 'danger' ? 'bg-error-50 text-error-500' : 'bg-brand-50 text-brand-500',
              )}
            >
              <Icon name={tone === 'danger' ? 'alert' : 'checkCircle'} className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <RadixDialog.Title className="text-base font-semibold text-admin-900">
                {title}
              </RadixDialog.Title>
              {description && (
                <RadixDialog.Description asChild>
                  <div className="mt-1.5 text-sm leading-relaxed text-admin-600">{description}</div>
                </RadixDialog.Description>
              )}
            </div>
          </div>

          {children && <div className="mt-4">{children}</div>}

          {requireTyped && (
            <div className="mt-4">
              <label htmlFor={typedId} className="block text-sm font-medium text-admin-700">
                {requireTypedLabel ?? (
                  <>
                    Type <span className="font-mono font-semibold">{requireTyped}</span> to confirm
                  </>
                )}
              </label>
              <input
                id={typedId}
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                autoComplete="off"
                spellCheck={false}
                className="mt-1.5 h-9 w-full rounded-lg border border-admin-200 px-3 font-mono text-sm text-admin-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
              />
            </div>
          )}

          {error != null && (
            <p
              role="alert"
              className="mt-4 rounded-lg bg-error-50 px-3 py-2 text-sm text-error-700"
            >
              {errorMessage(error)}
            </p>
          )}

          <div className="mt-6 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={busy}
              className="rounded-lg border border-admin-200 px-3.5 py-2 text-sm font-medium text-admin-700 transition-colors hover:bg-admin-50 disabled:opacity-50"
            >
              {cancelLabel}
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={!canConfirm}
              className={cn(
                'inline-flex items-center gap-2 rounded-lg px-3.5 py-2 text-sm font-semibold text-white transition-colors disabled:cursor-not-allowed disabled:opacity-40',
                tone === 'danger'
                  ? 'bg-error-500 hover:bg-error-700'
                  : 'bg-brand-500 hover:bg-brand-600',
              )}
            >
              {busy && (
                <span
                  className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white"
                  aria-hidden
                />
              )}
              {busy ? 'Working…' : confirmLabel}
            </button>
          </div>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

/** Re-export so callers building custom dialogs need one import site. */
export { Button };
